/* eslint-disable no-shadow */
import { createSelector } from '@reduxjs/toolkit';

import { type Promotion } from 'schemas';
import { type ReduxSelector } from '../../store.types';
import { findBestMatchingPromotion } from './promoteUtils';
import { PROMOTIONS_SLICE_NAME } from '../slicesNames';
import * as selectResults from '../results/selectResults';
import { PromotionsState } from './promotion.types';

// Basic selectors

/** The entire promotions slice */
export const slice: ReduxSelector<PromotionsState> = (state) => state[PROMOTIONS_SLICE_NAME];

/** The array of all promotions fetched */
export const promotions: ReduxSelector<Promotion[]> = (state) =>
  state[PROMOTIONS_SLICE_NAME].promotions;

/** The loading state of the fetch call for promotions */
export const isLoading: ReduxSelector<boolean> = (state) => state[PROMOTIONS_SLICE_NAME].isLoading;

/** Was a fetch call for promotions completed - true on both success and failure */
export const isLoaded: ReduxSelector<boolean> = (state) => state[PROMOTIONS_SLICE_NAME].isLoaded;

/** The error message related to the promotions fetch call */
export const error: ReduxSelector<string | null> = (state) => state[PROMOTIONS_SLICE_NAME].error;

// Advanced Selectors

/** Selects a promotions from the promotions array based on the promotionId field */
export function byId(id: number | string): ReduxSelector<Promotion | undefined> {
  let parsedId: number;
  if (typeof id === 'string') {
    parsedId = parseInt(id, 10);
  } else {
    parsedId = id;
  }
  return createSelector([promotions], (promotionsArray: Promotion[]) =>
    promotionsArray.find((p) => p.id === parsedId)
  );
}

/** The promotions whose locations are within the given radius. Relies on the distanceInMiles field to be applied to the locations array on the promotion. */
export const filteredByRadius: ReduxSelector<Promotion[]> = createSelector(
  [promotions],
  (promotions) => {
    const filteredPromotions = promotions.filter((promote) => {
      const { locations = [], radius = Infinity } = promote;

      // if there are no locations listed, include this promotion
      if (locations.length === 0 || radius === 0) return true;

      for (const location of locations) {
        // iterate through locations. If ANY location is within the radius, include this promote
        if (typeof location.distanceInMiles === 'number' && location.distanceInMiles < radius)
          return true;
      }

      // if no location was included in radius, exclude this promote
      return false;
    });

    return filteredPromotions;
  }
);

/** The promotions that are visible on the landing page (excludes those with hideOnLandingPage set to true). */
export const visibleOnLandingPage: ReduxSelector<Promotion[]> = createSelector(
  [promotions],
  (promotions: Promotion[]) => promotions.filter((promote) => !promote.hideOnLandingPage)
);

/** The promotions filtered by radius and excluding those with hideOnLandingPage flag set to true.
 * Used specifically for displaying promotions on the landing page. */
export const filteredByRadiusForLandingPage: ReduxSelector<Promotion[]> = createSelector(
  [filteredByRadius, visibleOnLandingPage],
  (radiusFiltered: Promotion[], landingPageVisible: Promotion[]) =>
    radiusFiltered.filter((promote) =>
      landingPageVisible.some((visible) => visible.id === promote.id)
    )
);

/** True if there are no promotions in the radius */
export const noPromotionsInRadius: ReduxSelector<boolean> = createSelector(
  [filteredByRadius, isLoading],
  (promotions: Promotion[], isLoading: boolean) => !isLoading && promotions?.length === 0
);

/**
 * @param specialtyIdOverride Optionally pass a specialtyId to select a promotion from. If no value is passed, it will default to using the specialtyId from the resultsSlice
 * @param subspecialtyIdOverride Optionally pass a subspecialtyId to select a promotion from. If no value is passed, it will default to using the subspecialtyId from the resultsSlice
 *
 *
 * @example
 * // getting a promotion using the resultsSlice specialty & subspecialty
 * const selectedBenefit = useSelector(selectPromotions.fromSpecialtyIds());
 *
 * // or manually pass in a specialtyId/subspecialtyId
 * const mySpecialtyId = 1;
 * const mySubspecialtyId = 2;
 * const mySpecialtyBenefit = useSelector(selectPromotions.fromSpecialtyIds(mySpecialtyId, mySubspecialtyId));
 *
 * @TODO TypesScript - the input selectors from selectResults have not been typed yet. Revisit this once the resultsSlice is typed.
 */
export function fromSpecialtyIds(
  specialtyIdOverride?: number,
  subspecialtyIdOverride?: number
): ReduxSelector<Promotion | null> {
  return createSelector(
    [filteredByRadius, selectResults.specialtyId, selectResults.subspecialtyId],
    (benefits: Promotion[], resultSpecialtyId: number, resultSubspecialtyId: number) => {
      // return null to show no promote
      if (benefits.length === 0) return null;

      const specialtyId = specialtyIdOverride ?? resultSpecialtyId;
      const subspecialtyId = subspecialtyIdOverride ?? resultSubspecialtyId;

      const options = { specialtyId, subspecialtyId };
      return findBestMatchingPromotion(benefits, options);
    }
  );
}

/**
 * Returns a list of promotions that appear at the top of a set of search results.
 * @TODO TypesScript - the input selectors from selectResults have not been typed yet. Revisit this once the resultsSlice is typed.
 */
export const featuredBenefitList: ReduxSelector<Promotion[]> = createSelector(
  [
    filteredByRadius,
    selectResults.specialtyId,
    selectResults.subspecialtyId,
    selectResults.highestFilterCountSpecialtyId,
    selectResults.wasServiceSearch,
  ],
  (benefits, resultSpecialtyId, resultSubspecialtyId, filterCountSpecialtyId, wasServiceSearch) => {
    const options = { subspecialtyId: resultSubspecialtyId, specialtyId: undefined }; // initialize the options object with the subspecialtyId from results

    // for service searches, we want to infer the specialty id, based on the specialty with the highest count from filter counts
    if (wasServiceSearch && filterCountSpecialtyId) {
      options.specialtyId = filterCountSpecialtyId;
    } else {
      // if it was not a service search, use the specialtyId from results
      options.specialtyId = resultSpecialtyId;
    }
    const benefitList: Promotion[] = []; // initializing this as an array, even though currently we only show 1 benefit. Eventually we will have the ability to show more.

    // in the future, we can refactor here and add logic for running this function multiple times to build a list of multiple benefits order from best matching to least matching
    const bestMatchingBenefit = findBestMatchingPromotion(benefits, options);
    if (bestMatchingBenefit) benefitList.push(bestMatchingBenefit);

    return benefitList;
  }
);
